import * as React from 'react'
import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Field, Form } from 'react-final-form'
import { useNotify, useSafeSetState, useTranslate, useRedirect } from 'ra-core'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import qs from 'qs'
import { Loading } from 'react-admin'
import AuthPageLayout from '../../components/AuthPageLayout'
import httpClient from '../../providers/httpClient'
import envConfig from '../../environments'
import { useValidResetPasswordLink } from '../../hooks/useValidResetPasswordLink'

const useStyles = makeStyles(theme => ({
  form: {
    padding: '8px 0 0',
  },
  input: {
    marginTop: '1em',
  },
  button: {
    width: '100%',
    padding: '12px 0',
    borderRadius: 10,
    fontWeight: 600,
  },
  formIcon: {
    marginRight: theme.spacing(1),
  },
}))

const Input = ({
  meta: { touched, error }, // eslint-disable-line react/prop-types
  input: inputProps, // eslint-disable-line react/prop-types
  ...props
}) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    variant="outlined"
    {...inputProps}
    {...props}
    fullWidth
  />
)

const ResetPassword = props => {
  const classes = useStyles()
  const translate = useTranslate()
  const [loading, setLoading] = useSafeSetState(false)
  const redirect = useRedirect()
  const notify = useNotify()
  const { isValidLink, isValidating } = useValidResetPasswordLink(
    props.location,
  )

  const validate = values => {
    const errors = { password: undefined, confirmPassword: undefined }
    if (!values.password) {
      errors.password = translate('ra.validation.required')
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = translate('ra.validation.required')
    }
    if (values.confirmPassword !== values.password) {
      errors.confirmPassword = translate('surveys.validations.confirmPassword')
    }
    return errors
  }

  const submit = values => {
    setLoading(true)
    values.email = qs.parse(props.location.search, {
      ignoreQueryPrefix: true,
    }).email

    httpClient(`${envConfig.backendUrl}/reset-password`, {
      method: 'POST',
      body: JSON.stringify(values),
    })
      .then(() => {
        notify('surveys.messages.change_password_success', { type: 'success' })
        redirect('/login')
      })
      .catch(() => {
        notify('surveys.messages.change_password_error', { type: 'warning' })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (isValidating) {
    return <Loading />
  }
  if (!isValidLink) {
    redirect('/login')
    return null
  }

  return (
    <AuthPageLayout
      title={translate('surveys.messages.create_password')}
      subtitle={translate('surveys.messages.create_password_text')}
    >
      <Form
        onSubmit={submit}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.form}>
              <div className={classes.input}>
                <Field
                  id="password"
                  name="password"
                  component={Input}
                  label={translate('ra.auth.password')}
                  type="password"
                  disabled={loading}
                />
              </div>
              <div className={classes.input}>
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  component={Input}
                  label={translate('surveys.messages.confirm_password')}
                  type="password"
                  disabled={loading}
                />
              </div>
            </div>
            <CardActions style={{ padding: '16px 0 0' }}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                className={classes.button}
              >
                {loading && (
                  <CircularProgress
                    className={classes.formIcon}
                    size={18}
                    thickness={2}
                  />
                )}
                {translate('surveys.messages.change_password')}
              </Button>
            </CardActions>
            <Typography
              variant="body2"
              color="primary"
              style={{ marginTop: 16, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => redirect('/login')}
            >
              {translate('ra.action.back')}
            </Typography>
          </form>
        )}
      />
    </AuthPageLayout>
  )
}

ResetPassword.propTypes = {
  location: PropTypes.any,
}

export default ResetPassword
